/** @format */

import { ReactNode, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Aggregation,
	joinMultiValue,
	splitMultiValue,
	splitPackedMultiValue,
	SpecificationThemeAggregation,
} from "@question-bank/shared";

interface SpecificationChangePayload {
	themes?: string;
	topics?: string;
	subTopics?: string;
}

interface FilterPanelProps {
	subjects?: Aggregation[];
	levels?: Aggregation[];
	tiers?: Aggregation[];
	specifications?: SpecificationThemeAggregation[];
	assessmentObjectives?: Aggregation[];
	years?: Aggregation[];
	selectedSubject?: string;
	selectedLevel?: string;
	selectedTier?: string;
	selectedSpecification?: string;
	selectedSpecificationTheme?: string;
	selectedSpecificationSubTopic?: string;
	selectedAssessmentObjective?: string;
	selectedFromYear?: number;
	selectedToYear?: number;
	totalQuestions?: number;
	onSubjectChange?: (subject?: string) => void;
	onLevelChange?: (level?: string) => void;
	onTierChange?: (tier?: string) => void;
	onSpecificationChange?: (selection: SpecificationChangePayload) => void;
	onAssessmentObjectiveChange?: (assessmentObjective?: string) => void;
	onYearRangeChange?: (fromYear?: number, toYear?: number) => void;
	onReset?: () => void;
}

interface CheckboxListProps {
	items?: Aggregation[];
	selected?: string[];
	emptyMessage: string;
	onChange: (values: string[]) => void;
}

const CheckboxList = ({ items, selected, emptyMessage, onChange }: CheckboxListProps) => {
	if (!items || items.length === 0) {
		return <p className="text-sm text-(--public-color-text-lighter)">{emptyMessage}</p>;
	}

	const selectedSet = new Set(selected ?? []);

	return (
		<ul className="space-y-2 pt-4">
			{items.map((item) => {
				const isChecked = selectedSet.has(item.key);
				const isDisabled = item.count === 0 && !isChecked;
				return (
					<li key={item.key}>
						<label
							className={`flex items-center gap-3 text-base font-roboto ${
								isDisabled
									? "cursor-not-allowed text-(--public-color-text-lighter) opacity-50"
									: "cursor-pointer text-(--public-color-text-default)"
							}`}
						>
							<input
								type="checkbox"
								className="h-4 w-4"
								checked={isChecked}
								disabled={isDisabled}
								onChange={() => {
									const next = new Set(selectedSet);
									if (isChecked) {
										next.delete(item.key);
									} else {
										next.add(item.key);
									}
									onChange(Array.from(next));
								}}
							/>
							<span className="capitalize">{item.key}</span>
							<span className="text-(--public-color-text-lighter)">({item.count})</span>
						</label>
					</li>
				);
			})}
		</ul>
	);
};

const buttonStyle =
	"text-base border w-[67px] h-[38px] rounded-md cursor-pointer pt-[6px] pr-[12px] pb-[6px] pl-[12px]";

const FILTER_KEYS = ["qualification", "specification", "tier", "year", "assessmentObjectives"] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
type OpenKey = FilterKey | null;

interface SpecificationSelection {
	themes: string[];
	topics: string[];
	subTopics: string[];
}

interface SpecificationTreeProps {
	themes?: SpecificationThemeAggregation[];
	selection: SpecificationSelection;
	onChange: (next: SpecificationSelection) => void;
}

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		fill="none"
		viewBox="0 0 24 24"
		strokeWidth={2}
		stroke="currentColor"
		className={`size-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
		aria-hidden
	>
		<path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
	</svg>
);

const SubjectIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.8}
		strokeLinecap="round"
		strokeLinejoin="round"
		className="size-5"
		aria-hidden
	>
		<rect x="3" y="3" width="7" height="7" rx="1.5" />
		<rect x="14" y="3" width="7" height="7" rx="1.5" />
		<rect x="3" y="14" width="7" height="7" rx="1.5" />
		<rect x="14" y="14" width="7" height="7" rx="1.5" />
	</svg>
);

const BusinessIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" fill="none" className="size-5" aria-hidden>
		<g clipPath="url(#business-icon-clip)">
			<path
				d="M49.97 10.91H38.08C37.43 4.86 32.24 0.04 26.09 0C26.06 0 26.03 0 26 0C19.8 0 14.57 4.8 13.92 10.91H2.03C1.03 10.91 0.209999 11.73 0.209999 12.73V50.18C0.209999 51.18 1.03 52 2.03 52H49.97C50.97 52 51.79 51.18 51.79 50.18V12.73C51.79 11.73 50.97 10.91 49.97 10.91ZM42.38 12.89V50.01H10.81V12.9H42.38V12.89ZM26.07 1.98C31.15 2.02 35.45 5.94 36.09 10.91H15.9C16.54 5.87 20.9 1.92 26.07 1.98ZM2.19 12.9H8.82V50.02H2.19V12.9ZM49.8 50.02H44.36V12.9H49.8V50.02Z"
				fill="currentColor"
			/>
			<path
				d="M21.06 32.33H21.78V36.29L20.63 36.63C20.14 36.79 19.89 37.17 19.89 37.62C19.89 38.16 20.32 38.59 20.95 38.59H30.8C31.48 38.59 32.01 38.07 32.01 37.4C32.01 36.73 31.47 36.19 30.8 36.19H24.6V32.35H29.14C29.77 32.35 30.31 31.83 30.31 31.18C30.31 30.53 29.77 30.01 29.14 30.01H24.6V28.01C24.6 27.07 24.87 26.32 25.34 25.83C25.77 25.43 26.28 25.2 27 25.2C28.03 25.2 28.71 25.63 29.41 26.37C29.59 26.57 29.9 26.8 30.4 26.8C31.16 26.8 31.77 26.22 31.77 25.45C31.77 25.02 31.59 24.71 31.34 24.46C30.35 23.4 29.02 22.64 26.96 22.64C25.34 22.64 24.06 23.13 23.18 24.03C22.28 24.93 21.79 26.3 21.79 27.94V30.01H21.07C20.42 30.01 19.9 30.55 19.9 31.18C19.9 31.81 20.42 32.35 21.07 32.35L21.06 32.33Z"
				fill="currentColor"
			/>
		</g>
		<defs>
			<clipPath id="business-icon-clip">
				<rect width="52" height="52" fill="white" />
			</clipPath>
		</defs>
	</svg>
);

const MathematicsIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" fill="none" className="size-5" aria-hidden>
		<path
			d="M32.33 14.16H47.66C48.49 14.16 49.16 13.49 49.16 12.66C49.16 11.83 48.49 11.16 47.66 11.16H32.33C31.5 11.16 30.83 11.83 30.83 12.66C30.83 13.49 31.5 14.16 32.33 14.16Z"
			fill="currentColor"
		/>
		<path
			d="M20.11 11.16H13.51V4.56C13.51 3.73 12.84 3.06 12.01 3.06C11.18 3.06 10.51 3.73 10.51 4.56V11.16H3.9C3.07 11.16 2.4 11.83 2.4 12.66C2.4 13.49 3.07 14.16 3.9 14.16H10.51V20.76C10.51 21.59 11.18 22.26 12.01 22.26C12.84 22.26 13.51 21.59 13.51 20.76V14.16H20.11C20.94 14.16 21.61 13.49 21.61 12.66C21.61 11.83 20.94 11.16 20.11 11.16Z"
			fill="currentColor"
		/>
		<path
			d="M18.8 33.13C18.21 32.54 17.26 32.54 16.68 33.13L12.01 37.8L7.34 33.13C6.75 32.54 5.8 32.54 5.22 33.13C4.63 33.72 4.63 34.67 5.22 35.25L9.89 39.92L5.22 44.59C4.63 45.18 4.63 46.13 5.22 46.71C5.51 47 5.9 47.15 6.28 47.15C6.66 47.15 7.05 47 7.34 46.71L12.01 42.04L16.68 46.71C16.97 47 17.36 47.15 17.74 47.15C18.12 47.15 18.51 47 18.8 46.71C19.39 46.12 19.39 45.17 18.8 44.59L14.13 39.92L18.8 35.25C19.39 34.66 19.39 33.71 18.8 33.13Z"
			fill="currentColor"
		/>
		<path
			d="M48.1 38.42H31.89C31.06 38.42 30.39 39.09 30.39 39.92C30.39 40.75 31.06 41.42 31.89 41.42H48.1C48.93 41.42 49.6 40.75 49.6 39.92C49.6 39.09 48.93 38.42 48.1 38.42Z"
			fill="currentColor"
		/>
		<path
			d="M38.06 32.82C38.06 33.89 38.92 34.75 39.99 34.75C41.06 34.75 41.92 33.89 41.92 32.82C41.92 31.75 41.06 30.89 39.99 30.89C38.92 30.89 38.06 31.75 38.06 32.82Z"
			fill="currentColor"
		/>
		<path
			d="M39.99 48.95C41.0559 48.95 41.92 48.0859 41.92 47.02C41.92 45.9541 41.0559 45.09 39.99 45.09C38.9241 45.09 38.06 45.9541 38.06 47.02C38.06 48.0859 38.9241 48.95 39.99 48.95Z"
			fill="currentColor"
		/>
	</svg>
);

// TODO: replace with subject metadata from the API once available.
const getSubjectIcon = (subject?: string): ReactNode => {
	const value = subject?.toLowerCase() ?? "";
	if (value.includes("business")) {
		return <BusinessIcon />;
	}
	if (value.includes("math")) {
		return <MathematicsIcon />;
	}
	return <SubjectIcon />;
};

const buildSpecificationSelectionValue = (theme?: string, topic?: string, subTopic?: string): string => {
	const parts = [theme?.trim(), topic?.trim(), subTopic?.trim()].filter(Boolean);
	return parts.join("::");
};

const matchesSpecificationSelection = (value: string, theme?: string, topic?: string, subTopic?: string): boolean => {
	const expected = buildSpecificationSelectionValue(theme, topic, subTopic);
	return value === expected || value === theme || value === topic || value === subTopic;
};

const computeInitiallyOpenThemes = (
	themes: SpecificationThemeAggregation[] | undefined,
	selection: SpecificationSelection,
): Set<string> => {
	const set = new Set<string>();
	if (!themes) {
		return set;
	}
	for (const themeKey of selection.themes) {
		set.add(themeKey);
	}
	for (const theme of themes) {
		const hasMatchingTopic = theme.topics.some((tp) =>
			selection.topics.some((value) => matchesSpecificationSelection(value, theme.key, tp.key)),
		);
		const hasMatchingSubTopic = theme.topics.some((tp) =>
			tp.subTopics.some((s) =>
				selection.subTopics.some((value) => matchesSpecificationSelection(value, theme.key, tp.key, s.key)),
			),
		);
		if (hasMatchingTopic || hasMatchingSubTopic) {
			set.add(theme.key);
		}
	}
	return set;
};

const SpecificationSubTopicRow = ({
	themeKey,
	topicKey,
	subTopic,
	isChecked,
	onToggle,
}: {
	themeKey: string;
	topicKey: string;
	subTopic: { key: string; count: number };
	isChecked: boolean;
	onToggle: (key: string) => void;
}) => {
	const isDisabled = subTopic.count === 0 && !isChecked;
	return (
		<li>
			<label
				className={`flex items-start gap-3 text-sm font-roboto ${
					isDisabled
						? "cursor-not-allowed text-(--public-color-text-lighter) opacity-50"
						: "cursor-pointer text-(--public-color-text-default)"
				}`}
			>
				<input
					type="checkbox"
					className="h-4 w-4 mt-0.5 shrink-0"
					checked={isChecked}
					disabled={isDisabled}
					onChange={() => onToggle(buildSpecificationSelectionValue(themeKey, topicKey, subTopic.key))}
					aria-label={`Select sub-topic ${subTopic.key}`}
				/>
				<span className="flex-1">{subTopic.key}</span>
				<span className="shrink-0 text-(--public-color-text-lighter)">({subTopic.count})</span>
			</label>
		</li>
	);
};

const SpecificationTopicRow = ({
	themeKey,
	topic,
	selection,
	onSelectTopic,
	onSelectSubTopic,
}: {
	themeKey: string;
	topic: SpecificationThemeAggregation["topics"][number];
	selection: SpecificationSelection;
	onSelectTopic: (key: string) => void;
	onSelectSubTopic: (key: string) => void;
}) => {
	const isTopicChecked = selection.topics.some((value) => matchesSpecificationSelection(value, themeKey, topic.key));
	const isDisabled = topic.count === 0 && !isTopicChecked;

	return (
		<li>
			<div className="flex items-start justify-between">
				<label
					className={`flex items-start gap-3 text-base font-roboto ${
						isDisabled
							? "cursor-not-allowed text-(--public-color-text-lighter) opacity-50"
							: "cursor-pointer text-(--public-color-text-default)"
					}`}
				>
					<input
						type="checkbox"
						className="h-4 w-4 mt-1.5 shrink-0"
						checked={isTopicChecked}
						disabled={isDisabled}
						onChange={() => onSelectTopic(buildSpecificationSelectionValue(themeKey, topic.key))}
						aria-label={`Select topic ${topic.key}`}
					/>
					<span className="flex-1">{topic.key}</span>
					<span className="shrink-0 text-(--public-color-text-lighter)">({topic.count})</span>
				</label>
			</div>
			{topic.subTopics.length > 0 && (
				<ul className="ml-7 mt-2 space-y-1.5">
					{topic.subTopics.map((sub) => (
						<SpecificationSubTopicRow
							key={sub.key}
							themeKey={themeKey}
							topicKey={topic.key}
							subTopic={sub}
							isChecked={selection.subTopics.some((value) =>
								matchesSpecificationSelection(value, themeKey, topic.key, sub.key),
							)}
							onToggle={onSelectSubTopic}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

const SpecificationThemeRow = ({
	theme,
	isOpen,
	selection,
	onToggleOpen,
	onSelectTheme,
	onSelectTopic,
	onSelectSubTopic,
}: {
	theme: SpecificationThemeAggregation;
	isOpen: boolean;
	selection: SpecificationSelection;
	onToggleOpen: (key: string) => void;
	onSelectTheme: (key: string) => void;
	onSelectTopic: (key: string) => void;
	onSelectSubTopic: (key: string) => void;
}) => {
	const isThemeChecked = selection.themes.some((value) => matchesSpecificationSelection(value, theme.key));
	const isDisabled = theme.count === 0 && !isThemeChecked;
	return (
		<li className="rounded-md border border-(--public-color-border-filter) bg-white">
			<div className="flex items-center justify-between px-4 py-3">
				<label
					className={`flex items-center gap-3 text-base font-roboto ${
						isDisabled
							? "cursor-not-allowed text-(--public-color-text-lighter) opacity-50"
							: "cursor-pointer text-(--public-color-btn-filters-bg)"
					}`}
				>
					<input
						type="checkbox"
						className="h-4 w-4"
						checked={isThemeChecked}
						disabled={isDisabled}
						onChange={() => onSelectTheme(buildSpecificationSelectionValue(theme.key))}
						aria-label={`Select theme ${theme.key}`}
					/>
					<span>{theme.key}</span>
					<span className="text-(--public-color-text-lighter)">({theme.count})</span>
				</label>
				<button
					type="button"
					onClick={() => onToggleOpen(theme.key)}
					aria-label={`${isOpen ? "Collapse" : "Expand"} theme ${theme.key}`}
					aria-expanded={isOpen}
					className="cursor-pointer p-1 text-(--public-color-text-default)"
				>
					<ChevronIcon isOpen={isOpen} />
				</button>
			</div>
			{isOpen && (
				<ul className="border-t border-(--public-color-border-filter) px-4 py-3 space-y-2">
					{theme.topics.length === 0 && <li className="text-sm text-(--public-color-text-lighter)">No topics</li>}
					{theme.topics.map((topic) => (
						<SpecificationTopicRow
							key={topic.key}
							themeKey={theme.key}
							topic={topic}
							selection={selection}
							onSelectTopic={onSelectTopic}
							onSelectSubTopic={onSelectSubTopic}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

const SpecificationTree = ({ themes, selection, onChange }: SpecificationTreeProps) => {
	const [openThemes, setOpenThemes] = useState<Set<string>>(() => computeInitiallyOpenThemes(themes, selection));

	if (!themes || themes.length === 0) {
		return <p className="text-sm text-(--public-color-text-lighter)">No specifications available</p>;
	}

	const toggleTheme = (key: string) => {
		setOpenThemes((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const toggleInArray = (values: string[], key: string): string[] =>
		values.includes(key) ? values.filter((v) => v !== key) : [...values, key];
	const setTheme = (key: string) => {
		onChange({ ...selection, themes: toggleInArray(selection.themes, key) });
	};
	const setTopic = (key: string) => {
		onChange({ ...selection, topics: toggleInArray(selection.topics, key) });
	};
	const setSubTopic = (key: string) => {
		onChange({ ...selection, subTopics: toggleInArray(selection.subTopics, key) });
	};

	return (
		<ul className="space-y-3 pt-4">
			{themes.map((theme) => (
				<SpecificationThemeRow
					key={theme.key}
					theme={theme}
					isOpen={openThemes.has(theme.key)}
					selection={selection}
					onToggleOpen={toggleTheme}
					onSelectTheme={setTheme}
					onSelectTopic={setTopic}
					onSelectSubTopic={setSubTopic}
				/>
			))}
		</ul>
	);
};

interface YearRangeSliderProps {
	years?: Aggregation[];
	fromYear?: number;
	toYear?: number;
	onChange: (fromYear?: number, toYear?: number) => void;
}

const CURRENT_YEAR = new Date().getUTCFullYear();
const DEFAULT_MIN_YEAR = CURRENT_YEAR - 10;
const DEFAULT_MAX_YEAR = CURRENT_YEAR;

const YearRangeSlider = ({ years, fromYear, toYear, onChange }: YearRangeSliderProps) => {
	const available = (years ?? []).map((a) => Number.parseInt(a.key, 10)).filter((n) => !Number.isNaN(n));
	const minYear = available.length > 0 ? Math.min(...available) : DEFAULT_MIN_YEAR;
	const maxYear = available.length > 0 ? Math.max(...available) : DEFAULT_MAX_YEAR;
	const from = fromYear ?? minYear;
	const to = toYear ?? maxYear;
	const safeFrom = Math.min(Math.max(from, minYear), maxYear);
	const safeTo = Math.min(Math.max(to, minYear), maxYear);

	const span = Math.max(maxYear - minYear, 1);
	const fromPct = ((safeFrom - minYear) / span) * 100;
	const toPct = ((safeTo - minYear) / span) * 100;

	const lo = Math.min(safeFrom, safeTo);
	const hi = Math.max(safeFrom, safeTo);
	const questionCount = (years ?? []).reduce((sum, a) => {
		const year = Number.parseInt(a.key, 10);
		if (Number.isNaN(year) || year < lo || year > hi) {
			return sum;
		}
		return sum + a.count;
	}, 0);

	const commit = (nextFrom: number, nextTo: number) => {
		const nextLo = Math.min(nextFrom, nextTo);
		const nextHi = Math.max(nextFrom, nextTo);
		onChange(nextLo === minYear ? undefined : nextLo, nextHi === maxYear ? undefined : nextHi);
	};

	return (
		<div className="pt-12 pb-4 px-4">
			<div className="relative h-10">
				<span
					aria-hidden
					className="absolute -top-9 -translate-x-1/2 inline-flex items-center justify-center min-w-12 h-7 px-2 rounded-md bg-(--public-color-theme-primary) text-white text-sm font-semibold after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-4 after:border-transparent after:border-t-(--public-color-theme-primary)"
					style={{ left: `calc(${fromPct}% + ${(0.5 - fromPct / 100) * 32}px)` }}
				>
					{safeFrom}
				</span>
				<span
					aria-hidden
					className="absolute -top-9 -translate-x-1/2 inline-flex items-center justify-center min-w-12 h-7 px-2 rounded-md bg-(--public-color-theme-primary) text-white text-sm font-semibold after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-4 after:border-transparent after:border-t-(--public-color-theme-primary)"
					style={{ left: `calc(${toPct}% + ${(0.5 - toPct / 100) * 32}px)` }}
				>
					{safeTo}
				</span>
				<div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 bg-gray-200 rounded-full" />
				<div
					className="absolute top-1/2 -translate-y-1/2 h-3 bg-(--public-color-theme-primary) rounded-full"
					style={{ left: `${fromPct}%`, right: `${100 - toPct}%` }}
				/>
				<input
					type="range"
					aria-label="Start year"
					min={minYear}
					max={maxYear}
					step={1}
					value={safeFrom}
					onChange={(e) => commit(Number.parseInt(e.target.value, 10), safeTo)}
					className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-8 [&::-webkit-slider-thumb]:w-8 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-(--public-color-theme-primary) [&::-webkit-slider-thumb]:border-5 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.25)] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-10 [&::-moz-range-thumb]:w-10 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-(--public-color-theme-primary) [&::-moz-range-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.25)] [&::-moz-range-thumb]:cursor-pointer"
				/>
				<input
					type="range"
					aria-label="End year"
					min={minYear}
					max={maxYear}
					step={1}
					value={safeTo}
					onChange={(e) => commit(safeFrom, Number.parseInt(e.target.value, 10))}
					className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-8 [&::-webkit-slider-thumb]:w-8 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-(--public-color-theme-primary) [&::-webkit-slider-thumb]:border-5 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.25)] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-10 [&::-moz-range-thumb]:w-10 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-(--public-color-theme-primary) [&::-moz-range-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.25)] [&::-moz-range-thumb]:cursor-pointer"
				/>
			</div>
			<div
				className="pt-4 flex items-center justify-center gap-2 text-base font-roboto text-(--public-color-text-filter)"
				aria-live="polite"
			>
				<span className="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-(--public-color-theme-primary) text-white text-sm font-semibold">
					{questionCount}
				</span>
			</div>
		</div>
	);
};

const FILTER_ICON_PATH =
	"M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75";

interface FilterDropdownProps {
	id: FilterKey;
	label: string;
	activeCount: number;
	isOpen: boolean;
	onToggle: (id: FilterKey) => void;
	panelClassName?: string;
	children: ReactNode;
}

const FilterDropdown = ({
	id,
	label,
	activeCount,
	isOpen,
	onToggle,
	panelClassName = "w-80",
	children,
}: FilterDropdownProps) => {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; maxHeight: number }>();

	useEffect(() => {
		if (!isOpen || !buttonRef.current) {
			return;
		}

		const MAX_PANEL_HEIGHT = 420;
		const updatePosition = () => {
			const rect = buttonRef.current!.getBoundingClientRect();
			setPanelStyle({
				top: rect.bottom + 8,
				left: rect.left,
				maxHeight: Math.min(window.innerHeight - rect.bottom - 16, MAX_PANEL_HEIGHT),
			});
		};
		updatePosition();
		window.addEventListener("resize", updatePosition);
		return () => window.removeEventListener("resize", updatePosition);
	}, [isOpen]);

	return (
		<div className="relative">
			<button
				ref={buttonRef}
				type="button"
				aria-haspopup="true"
				aria-expanded={isOpen}
				aria-label={`${label} filter`}
				onClick={() => onToggle(id)}
				className={`relative z-20 inline-flex h-10 items-center gap-2 rounded-md border bg-white px-4 font-roboto text-[16px] cursor-pointer text-(--public-color-text-filter) ${
					isOpen || activeCount > 0 ? "border-(--public-color-theme-primary)" : "border-(--public-color-border-filter)"
				}`}
			>
				<span>{label}</span>
				<ChevronIcon isOpen={isOpen} />
			</button>
			{activeCount > 0 && (
				<span
					aria-label={`${activeCount} active ${label} filters`}
					className="absolute -top-2 -right-2 z-20 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-(--public-color-theme-primary) px-1 text-xs font-semibold text-white"
				>
					{activeCount}
				</span>
			)}
			{isOpen && panelStyle && (
				<div
					style={{ top: panelStyle.top, left: panelStyle.left, maxHeight: panelStyle.maxHeight }}
					className={`fixed z-30 overflow-y-auto rounded-lg border border-(--public-color-border-filter) bg-white p-4 shadow-lg ${panelClassName}`}
				>
					{children}
				</div>
			)}
		</div>
	);
};

export const FilterPanel = ({
	subjects = [],
	levels = [],
	tiers = [],
	specifications = [],
	assessmentObjectives = [],
	years = [],
	selectedSubject = "",
	selectedLevel,
	selectedTier,
	selectedSpecification,
	selectedSpecificationTheme,
	selectedSpecificationSubTopic,
	selectedAssessmentObjective,
	selectedFromYear,
	selectedToYear,
	totalQuestions,
	onSubjectChange,
	onLevelChange,
	onTierChange,
	onSpecificationChange,
	onAssessmentObjectiveChange,
	onYearRangeChange,
	onReset,
}: FilterPanelProps) => {
	const { t } = useTranslation();
	const sortedSubjects = [...subjects].sort((a, b) => a.key.localeCompare(b.key));
	const sortedAssessmentObjectives = [...assessmentObjectives].sort((a, b) => a.key.localeCompare(b.key));

	const selectedLevels = splitMultiValue(selectedLevel);
	const selectedTiers = splitMultiValue(selectedTier);
	const selectedAssessmentObjectives = splitMultiValue(selectedAssessmentObjective);
	const specSelection: SpecificationSelection = {
		themes: splitPackedMultiValue(selectedSpecificationTheme),
		topics: splitPackedMultiValue(selectedSpecification),
		subTopics: splitPackedMultiValue(selectedSpecificationSubTopic),
	};
	const specificationCount = specSelection.themes.length + specSelection.topics.length + specSelection.subTopics.length;
	const isYearActive = selectedFromYear !== undefined || selectedToYear !== undefined;
	const totalActiveFilterCount =
		selectedLevels.length +
		specificationCount +
		selectedTiers.length +
		(isYearActive ? 1 : 0) +
		selectedAssessmentObjectives.length;
	const [openKey, setOpenKey] = useState<OpenKey>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<FilterKey>("qualification");

	const handleSubjectChange = (value: string) => {
		onSubjectChange?.(value || undefined);
	};
	const toggleDropdown = (key: FilterKey) => {
		setOpenKey((prev) => (prev === key ? null : key));
	};
	const closeDropdowns = () => setOpenKey(null);

	const handleLevelChange = (values: string[]) => onLevelChange?.(joinMultiValue(values));
	const handleTierChange = (values: string[]) => onTierChange?.(joinMultiValue(values));
	const handleAssessmentObjectiveChange = (values: string[]) => onAssessmentObjectiveChange?.(joinMultiValue(values));
	const handleSpecificationChange = (next: SpecificationSelection) =>
		onSpecificationChange?.({
			themes: joinMultiValue(next.themes),
			topics: joinMultiValue(next.topics),
			subTopics: joinMultiValue(next.subTopics),
		});

	const handleResetAll = () => {
		onReset?.();
	};

	const tabLabels: Record<FilterKey, string> = {
		qualification: t("filterPanel.qualificationTab"),
		specification: t("filterPanel.specificationTab"),
		tier: t("filterPanel.tierTab"),
		year: t("filterPanel.yearTab"),
		assessmentObjectives: t("filterPanel.assessmentObjectivesTab"),
	};

	const activeCountFor = (key: FilterKey): number => {
		switch (key) {
			case "qualification":
				return selectedLevels.length;
			case "specification":
				return specificationCount;
			case "tier":
				return selectedTiers.length;
			case "year":
				return isYearActive ? 1 : 0;
			case "assessmentObjectives":
				return selectedAssessmentObjectives.length;
		}
	};

	const panelClassFor = (key: FilterKey): string => {
		if (key === "specification") {
			return "w-[28rem]";
		}
		if (key === "year") {
			return "w-[24rem]";
		}
		return "w-80";
	};

	const renderFilterContent = (key: FilterKey): ReactNode => {
		switch (key) {
			case "qualification":
				return (
					<CheckboxList
						items={levels}
						selected={selectedLevels}
						emptyMessage="No qualifications available"
						onChange={handleLevelChange}
					/>
				);
			case "specification":
				return (
					<SpecificationTree themes={specifications} selection={specSelection} onChange={handleSpecificationChange} />
				);
			case "tier":
				return (
					<CheckboxList
						items={tiers}
						selected={selectedTiers}
						emptyMessage="No tiers available"
						onChange={handleTierChange}
					/>
				);
			case "year":
				return (
					<YearRangeSlider
						years={years}
						fromYear={selectedFromYear}
						toYear={selectedToYear}
						onChange={(fromYear, toYear) => onYearRangeChange?.(fromYear, toYear)}
					/>
				);
			case "assessmentObjectives":
				return (
					<CheckboxList
						items={sortedAssessmentObjectives}
						selected={selectedAssessmentObjectives}
						emptyMessage="No assessment objectives available"
						onChange={handleAssessmentObjectiveChange}
					/>
				);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative flex min-w-50 flex-1 basis-50 items-center">
					<span className="pointer-events-none absolute left-4 text-(--public-color-text-filter)">
						{getSubjectIcon(selectedSubject)}
					</span>
					<select
						id="subjects"
						aria-label={t("filterPanel.subjectsLabel")}
						value={selectedSubject}
						onChange={(e) => handleSubjectChange(e.target.value)}
						className="w-full appearance-none bg-white rounded-[10px] h-10 border border-(--public-color-border-filter) py-2 pl-12 pr-10 capitalize text-(--public-color-text-filter) cursor-pointer focus:border-(--public-color-theme-primary) focus:outline-none"
					>
						<option value="">{t("filterPanel.allSubjects")}</option>
						{sortedSubjects.map((subject) => (
							<option key={subject.key} value={subject.key}>
								{subject.key} ({subject.count})
							</option>
						))}
					</select>
					<span className="pointer-events-none absolute right-4 text-(--public-color-text-filter)">
						<ChevronIcon isOpen={false} />
					</span>
				</div>

				<div className="hidden md:flex flex-wrap items-center gap-3">
					{FILTER_KEYS.map((key) => (
						<FilterDropdown
							key={key}
							id={key}
							label={tabLabels[key]}
							activeCount={activeCountFor(key)}
							isOpen={openKey === key}
							onToggle={toggleDropdown}
							panelClassName={panelClassFor(key)}
						>
							{renderFilterContent(key)}
						</FilterDropdown>
					))}
					<button
						type="button"
						aria-label="reset all filters"
						onClick={handleResetAll}
						className="inline-flex h-10 items-center gap-2 rounded-md bg-(--public-color-btn-filters-bg) px-4 font-roboto text-[16px] cursor-pointer text-white"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 16 16"
							fill="currentColor"
							className="size-5"
							aria-hidden
						>
							<path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z" />
							<path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
						</svg>
						{t("filterPanel.resetButton")}
					</button>
				</div>

				<div className="relative md:hidden">
					<button
						type="button"
						aria-label="open filters"
						aria-expanded={isModalOpen}
						onClick={() => {
							closeDropdowns();
							setIsModalOpen(true);
						}}
						className="relative flex h-10 items-center justify-center gap-2 rounded-md py-1.5 px-3 font-roboto text-[16px] leading-relaxed text-white cursor-pointer bg-(--public-color-btn-filters-bg)"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth={1.5}
							stroke="currentColor"
							className="size-6"
						>
							<path strokeLinecap="round" strokeLinejoin="round" d={FILTER_ICON_PATH} />
						</svg>
						{t("filterPanel.filtersLabel")}
					</button>
					{totalActiveFilterCount > 0 && (
						<span
							aria-label={`${totalActiveFilterCount} active filters`}
							className="absolute -top-2 -right-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-(--public-color-theme-primary) px-1 text-xs font-semibold text-white"
						>
							{totalActiveFilterCount}
						</span>
					)}
				</div>
			</div>

			{openKey !== null && (
				<button
					aria-hidden="true"
					tabIndex={-1}
					className="fixed inset-0 z-10 cursor-default"
					onClick={closeDropdowns}
				/>
			)}

			{isModalOpen && (
				<>
					<button
						aria-label="dismiss filters"
						className="fixed inset-0 bg-black/15 z-40"
						onClick={() => setIsModalOpen(false)}
					/>
					<dialog
						open
						aria-modal="true"
						aria-label="Filters"
						className="fixed top-30 left-1/2 -translate-x-1/2 bg-white border border-gray-300 rounded-lg z-50 w-[90vw] max-w-160 max-h-[80vh] flex flex-col"
					>
						<div className="flex items-center justify-between border-b p-4 h-15.75 w-full border-(--public-color-border-filter) flex-none">
							<div className="flex items-center gap-2">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="size-6"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d={FILTER_ICON_PATH} />
								</svg>
								<h1 className="text-xl font-normal leading-relaxed text-(--public-color-text-filter) font-roboto">
									{t("filterPanel.filtersLabel")}
								</h1>
							</div>
							<button aria-label="close filters panel" onClick={() => setIsModalOpen(false)} className="cursor-pointer">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="size-8 opacity-50"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						<div
							role="tablist"
							className="flex gap-1 overflow-x-auto border-b border-(--public-color-border-filter) flex-none"
						>
							{FILTER_KEYS.map((key) => {
								const isActive = activeTab === key;
								const count = activeCountFor(key);
								return (
									<button
										key={key}
										role="tab"
										aria-selected={isActive}
										onClick={() => setActiveTab(key)}
										className={`flex items-center gap-1 whitespace-nowrap px-4 py-2 text-base font-roboto cursor-pointer border-b-2 ${
											isActive
												? "border-(--public-color-theme-primary) text-(--public-color-text-filter)"
												: "border-transparent text-(--public-color-text-lighter)"
										}`}
									>
										{tabLabels[key]}
										{count > 0 && (
											<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-(--public-color-theme-primary) px-1 text-xs font-semibold text-white">
												{count}
											</span>
										)}
									</button>
								);
							})}
						</div>

						<div className="min-h-40 p-4 flex-1 overflow-y-auto">{renderFilterContent(activeTab)}</div>

						<div className="flex items-center justify-between gap-2 w-full h-15.75 border-t border-(--public-color-border-filter) p-3 flex-none">
							<p className="flex items-center gap-2 text-base leading-relaxed font-lato text-(--public-color-text-filter)">
								{t("filterPanel.totalQuestions")}
								<span className="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-(--public-color-theme-primary) text-white text-sm font-semibold">
									{totalQuestions ?? "..."}
								</span>
							</p>
							<div className="flex gap-2">
								<button
									aria-label="reset filters"
									onClick={handleResetAll}
									className={`bg-white font-roboto border-(--public-color-btn-filters-bg) text-(--public-color-btn-filters-bg) ${buttonStyle}`}
								>
									{t("filterPanel.resetButton")}
								</button>
								<button
									aria-label="close filters"
									onClick={() => setIsModalOpen(false)}
									className={`text-white bg-(--public-color-theme-primary) font-roboto ${buttonStyle}`}
								>
									{t("filterPanel.closeButton")}
								</button>
							</div>
						</div>
					</dialog>
				</>
			)}
		</div>
	);
};
